import React from 'react';
import { ReferenceDot } from 'recharts';
import { CATEGORY_META, groupEventsByBucket } from './timelineUtils';

/**
 * Builds an array of <ReferenceDot/> elements to be rendered as
 * children inside a Recharts chart (Line/Area/Composed). One dot is
 * placed per X bucket; if multiple events share a bucket the dot uses
 * the colour of the most recent one and a small "+N" label.
 *
 * @param events    Array of timeline events.
 * @param chartData The same data array passed to the chart.
 * @param xKey      Key in chartData rows that matches the X axis.
 * @param yMax      Numeric Y position for the markers (top of chart).
 * @param bucketFn  Date -> bucket-key matching xKey values.
 */
export function buildTimelineDots({ events, chartData, xKey, yMax, bucketFn, onClick }) {
  if (!events?.length || !chartData?.length) return [];
  const validBuckets = new Set(chartData.map((d) => d[xKey]));
  const grouped = groupEventsByBucket(events, bucketFn);
  const dots = [];
  grouped.forEach((list, bucket) => {
    if (!validBuckets.has(bucket)) return;
    // Most recent event drives the colour.
    const latest = list.reduce(
      (a, b) => (new Date(b.occurred_at) > new Date(a.occurred_at) ? b : a),
      list[0],
    );
    const meta = CATEGORY_META[latest.category] || CATEGORY_META.manual;
    dots.push(
      <ReferenceDot
        key={`tl-${bucket}-${latest.id}`}
        x={bucket}
        y={yMax}
        r={6}
        fill={meta.color}
        stroke="#fff"
        strokeWidth={2}
        ifOverflow="extendDomain"
        isFront
        onClick={() => onClick?.(list)}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
        label={list.length > 1 ? {
          value: `+${list.length - 1}`,
          position: 'top',
          fontSize: 10,
          fill: meta.color,
          fontWeight: 700,
        } : undefined}
      />,
    );
  });
  return dots;
}
